<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { InstanceRow } from '@/components/InstancesTable.vue'
import type { ActivationCode, Customer, Instance, Product, SharedData } from '@/types'
import { Head, Link, router, useForm, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import InstancesTable from '@/components/InstancesTable.vue'
import { dashboardLayout } from '@/lib/layouts'
import {
  adminActivationCodePath,
  adminCustomerActivationCodesPath,
  adminCustomerPath,
  adminCustomersPath,
  adminProductPath,
  editAdminCustomerPath,
} from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  customer: Customer
  instances: Instance[]
  activationCodes: ActivationCode[]
  products: Pick<Product, 'id' | 'name' | 'slug'>[]
}>()

const page = usePage<SharedData>()
const issuedActivationCode = computed(() => page.props.flash?.activation_code)

const instanceRows = computed<InstanceRow[]>(() =>
  props.instances.map(instance => ({
    id: instance.id,
    name: instance.product.name,
    identifier: instance.identifier,
    metadata: instance.metadata,
    created_at: instance.created_at,
    to: adminProductPath(instance.product.slug),
  })),
)

const availableProducts = computed(() =>
  props.products.filter(
    product => !props.instances.some(instance => instance.product.id === product.id),
  ),
)
const productOptions = computed(() =>
  availableProducts.value.map(product => ({
    label: product.name,
    value: product.id,
  })),
)

const addOpen = ref(false)
const addForm = useForm<{ product_id: number | undefined }>({
  product_id: undefined,
})

function createActivationCode() {
  addForm.post(adminCustomerActivationCodesPath(props.customer.id), {
    preserveScroll: true,
    onSuccess: () => {
      addOpen.value = false
      addForm.reset()
    },
  })
}

const removeActivationCodeOpen = ref(false)
const removingActivationCode = ref<ActivationCode | null>(null)
const removingActivationCodeProcessing = ref(false)

function requestRemoveActivationCode(activationCode: ActivationCode) {
  removingActivationCode.value = activationCode
  removeActivationCodeOpen.value = true
}

function removeActivationCode() {
  if (!removingActivationCode.value)
    return

  router.delete(adminActivationCodePath(removingActivationCode.value.id), {
    preserveScroll: true,
    onStart: () => (removingActivationCodeProcessing.value = true),
    onFinish: () => {
      removingActivationCodeProcessing.value = false
      removeActivationCodeOpen.value = false
      removingActivationCode.value = null
    },
  })
}

function activationCodeStatus(activationCode: ActivationCode) {
  if (activationCode.used_at)
    return 'Used'

  return new Date(activationCode.expires_at) <= new Date() ? 'Expired' : 'Ready'
}

const deleteOpen = ref(false)
const deleting = ref(false)

function toggleActive() {
  router.patch(
    adminCustomerPath(props.customer.id),
    { active: !props.customer.active },
    { preserveScroll: true },
  )
}

function destroy() {
  router.delete(adminCustomerPath(props.customer.id), {
    onStart: () => (deleting.value = true),
    onFinish: () => (deleting.value = false),
  })
}

const menuItems = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: 'Edit',
      icon: 'i-lucide-pencil',
      onSelect: () => router.visit(editAdminCustomerPath(props.customer.id)),
    },
    {
      label: props.customer.active ? 'Deactivate' : 'Activate',
      icon: props.customer.active ? 'i-lucide-circle-pause' : 'i-lucide-circle-play',
      onSelect: toggleActive,
    },
    {
      label: 'Delete',
      icon: 'i-lucide-trash-2',
      color: 'error',
      onSelect: () => (deleteOpen.value = true),
    },
  ],
])

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString() : '—'
}
</script>

<template>
  <Head :title="customer.name" />

  <UDashboardPanel id="customer-show">
    <template #header>
      <UDashboardNavbar :title="customer.name">
        <template #leading>
          <UButton
            :to="adminCustomersPath()"
            :as="Link"
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
          />
        </template>

        <template #right>
          <UButton
            :to="editAdminCustomerPath(customer.id)"
            :as="Link"
            label="Edit"
            icon="i-lucide-pencil"
            color="neutral"
            variant="subtle"
          />
          <UDropdownMenu :items="menuItems">
            <UButton
              icon="i-lucide-ellipsis-vertical"
              color="neutral"
              variant="ghost"
              square
            />
          </UDropdownMenu>
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <div class="ring-default flex flex-col gap-5 rounded-lg p-5 ring">
          <div class="flex items-center gap-4">
            <UAvatar
              :alt="customer.name"
              size="lg"
              :ui="{ root: 'bg-elevated text-highlighted font-medium' }"
            />
            <div class="min-w-0">
              <p class="text-highlighted truncate text-lg font-semibold">
                {{ customer.name }}
              </p>
              <div class="mt-1 flex items-center gap-2">
                <UBadge
                  :color="customer.active ? 'success' : 'neutral'"
                  variant="subtle"
                  size="sm"
                >
                  {{ customer.active ? 'Active' : 'Inactive' }}
                </UBadge>
                <span class="text-muted text-sm">
                  {{ instances.length }}
                  {{ instances.length === 1 ? 'product instance' : 'product instances' }}
                </span>
              </div>
            </div>
          </div>

          <USeparator />

          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Created
              </dt>
              <dd class="text-highlighted">
                {{ formatDate(customer.created_at) }}
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-muted text-xs uppercase tracking-wide">
                Notes
              </dt>
              <dd class="text-highlighted whitespace-pre-line">
                {{ customer.notes || '—' }}
              </dd>
            </div>
          </dl>
        </div>

        <section class="flex flex-col gap-4">
          <div class="flex items-end justify-between gap-4">
            <div>
              <h2 class="text-highlighted font-semibold">
                Activation codes
              </h2>
              <p class="text-muted text-sm">
                Single-use product activation codes for this customer.
              </p>
            </div>
            <UButton
              label="Create code"
              icon="i-lucide-plus"
              size="sm"
              :disabled="availableProducts.length === 0"
              @click="addOpen = true"
            />
          </div>

          <UAlert
            v-if="issuedActivationCode"
            title="Activation code created"
            :description="issuedActivationCode"
            color="success"
            variant="soft"
            icon="i-lucide-key-round"
          />

          <div
            v-if="activationCodes.length === 0"
            class="ring-default text-muted rounded-lg p-6 text-center text-sm ring"
          >
            No activation codes yet.
          </div>

          <ul v-else class="divide-default ring-default divide-y rounded-lg ring">
            <li
              v-for="activationCode in activationCodes"
              :key="activationCode.id"
              class="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <Link
                  :href="adminProductPath(activationCode.product.slug)"
                  class="text-highlighted font-medium hover:underline"
                >
                  {{ activationCode.product.name }}
                </Link>
                <p class="text-muted text-sm">
                  Expires {{ formatDate(activationCode.expires_at) }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <UBadge
                  :color="activationCodeStatus(activationCode) === 'Ready' ? 'success' : 'neutral'"
                  variant="subtle"
                  size="sm"
                >
                  {{ activationCodeStatus(activationCode) }}
                </UBadge>
                <UButton
                  label="Remove"
                  color="error"
                  variant="ghost"
                  size="sm"
                  @click="requestRemoveActivationCode(activationCode)"
                />
              </div>
            </li>
          </ul>
        </section>

        <section class="flex flex-col gap-4">
          <div>
            <h2 class="text-highlighted font-semibold">
              Instances
            </h2>
            <p class="text-muted text-sm">
              Activated products this customer is licensed to use.
            </p>
          </div>

          <InstancesTable
            :rows="instanceRows"
            noun="product"
            empty-text="No instances yet."
          />
        </section>
      </div>
    </template>
  </UDashboardPanel>

  <UModal
    v-model:open="addOpen"
    title="Create activation code"
    description="Issue a single-use activation code for a product."
  >
    <template #body>
      <UFormField label="Product" name="product_id" required>
        <USelectMenu
          v-model="addForm.product_id"
          :items="productOptions"
          value-key="value"
          placeholder="Select a product"
          class="w-full"
        />
      </UFormField>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="addOpen = false"
        />
        <UButton
          label="Create code"
          icon="i-lucide-plus"
          :loading="addForm.processing"
          :disabled="!addForm.product_id"
          @click="createActivationCode"
        />
      </div>
    </template>
  </UModal>

  <UModal
    v-model:open="removeActivationCodeOpen"
    title="Remove activation code"
    :description="`Remove the activation code for ${removingActivationCode?.product.name ?? 'this product'}?`"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="removeActivationCodeOpen = false"
        />
        <UButton
          label="Remove"
          color="error"
          icon="i-lucide-trash-2"
          :loading="removingActivationCodeProcessing"
          @click="removeActivationCode"
        />
      </div>
    </template>
  </UModal>

  <UModal
    v-model:open="deleteOpen"
    title="Delete customer"
    :description="`Delete &quot;${customer.name}&quot;? This removes its instances and cannot be undone.`"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="deleteOpen = false"
        />
        <UButton
          label="Delete"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deleting"
          @click="destroy"
        />
      </div>
    </template>
  </UModal>
</template>
